import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, ChevronLeft, ChevronRight, Quote, ShieldCheck, Clock, MapPin } from "lucide-react";
import { Button } from "../components/ui/button";
import { Eyebrow, Reveal, SectionHeading, NumberedCard, ImageCard, CTA, Icon } from "../components/Shared";
import { IMAGES, STATS, SERVICES, AUDIENCES, DIFFERENTIATORS, FEATURED_CLIENTS } from "../mock";

const Hero = () => (
  <section className="relative flex min-h-[620px] items-end overflow-hidden lg:min-h-[720px]" data-testid="home-hero">
    <img src={IMAGES.hero} alt="Great North Advantage team working with a nonprofit board" className="absolute inset-0 h-full w-full object-cover hero-zoom" />
    <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/70 to-brand-900/30" />
    <div className="container-x relative z-10 grid gap-10 py-16 lg:grid-cols-12 lg:py-24">
      <div className="lg:col-span-8">
        <Reveal>
          <div className="inline-flex items-center gap-3 rounded-sm bg-brand-900/80 px-4 py-2 backdrop-blur">
            <span className="h-px w-6 bg-gold-300" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-sand-100">Shared administrative services for nonprofits · Family-owned since 1990</span>
          </div>
          <h1 className="mt-6 font-serif text-5xl leading-[1.02] text-white sm:text-6xl lg:text-7xl">
            Behind the scenes,
            <br />
            <em className="font-light text-gold-200">helping you every day.</em>
          </h1>
          <p className="mt-7 max-w-2xl text-lg leading-relaxed text-sand-200">
            Great North Advantage handles the financial, administrative and facilities needs of nonprofit organizations, backed by over 36 years of Great North experience. We serve as an extension of your staff and volunteers so you can stay focused on your mission.
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <Button asChild size="lg" className="btn-pill h-12 bg-gold-400 px-8 text-base text-ink-900 hover:bg-gold-300" data-testid="hero-contact-btn">
              <Link to="/contact-us">
                Contact Us <ArrowRight size={17} className="ml-2" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="btn-pill h-12 border-white/40 bg-transparent px-8 text-base text-white hover:bg-white/10 hover:text-white" data-testid="hero-services-btn">
              <Link to="/what-we-do">What we do</Link>
            </Button>
          </div>
        </Reveal>
      </div>
      <Reveal delay={150} className="lg:col-span-4 lg:self-end">
        <div className="rounded-2xl border border-white/15 bg-white/10 p-6 backdrop-blur-md">
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-sand-300">Nonprofit leaders</p>
          <p className="mt-2 font-serif text-xl leading-snug text-white">Free two-hour Operational &amp; Property Review for NH Center for Nonprofits members.</p>
          <Link to="/nonprofit-review" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-gold-200 transition-colors hover:text-white" data-testid="hero-review-link">
            Request your free review <ArrowUpRight size={15} />
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);

const ClientLogos = () => (
  <section className="border-b border-brand-100 bg-white" data-testid="client-logos">
    <div className="container-x flex flex-col items-center gap-6 py-8 lg:flex-row lg:justify-between">
      <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-ink-500">Trusted by New England nonprofits</p>
      <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
        {FEATURED_CLIENTS.map((c) => (
          <img key={c.name} src={c.logo} alt={c.name} title={c.name} className="h-12 w-auto object-contain opacity-80 transition-opacity duration-300 hover:opacity-100 lg:h-14" />
        ))}
        <span className="rounded-full border border-brand-100 px-3 py-1.5 text-xs font-medium text-ink-700">Backed by Great North's 350+ communities across NH, MA, ME &amp; RI</span>
      </div>
    </div>
  </section>
);

const Stats = () => (
  <section className="border-b border-brand-100 bg-white" data-testid="stats-section">
    <div className="container-x grid grid-cols-2 divide-brand-100 py-10 md:grid-cols-4 md:divide-x">
      {STATS.map((s, i) => (
        <Reveal key={s.label} delay={i * 80} className="px-4 py-4 md:px-8">
          <p className="font-serif text-4xl text-brand-700 lg:text-5xl">{s.value}</p>
          <p className="mt-2 text-sm text-ink-600">{s.label}</p>
        </Reveal>
      ))}
    </div>
    <div className="border-t border-brand-100 bg-sand-50">
      <div className="container-x flex flex-wrap items-center gap-x-10 gap-y-3 py-4 text-xs font-medium text-ink-600">
        <span className="inline-flex items-center gap-2"><ShieldCheck size={15} className="text-brand-600" /> AMO accredited since 2018 &middot; CAI Member &middot; BBB A+</span>
        <span className="inline-flex items-center gap-2"><Clock size={15} className="text-brand-600" /> 24/7 emergency maintenance, 30+ technicians</span>
        <span className="inline-flex items-center gap-2"><MapPin size={15} className="text-brand-600" /> Nine offices across NH, MA, ME &amp; RI</span>
      </div>
    </div>
  </section>
);

const Intro = () => (
  <section className="section" data-testid="intro-section">
    <div className="container-x grid items-center gap-12 lg:grid-cols-12">
      <Reveal className="lg:col-span-6">
        <SectionHeading
          eyebrow="Who we are"
          title="An extension of your staff and volunteers."
          intro="We successfully tackle the challenges that distract you, your staff and your Board of Directors from the work of achieving your organization's mission. Our hard work on your behalf ensures that you can remain focused on making a difference in our cities and towns."
        />
        <div className="mt-8 flex flex-wrap gap-3">
          <Button asChild className="btn-pill bg-brand-600 text-white hover:bg-brand-700">
            <Link to="/who-we-are">About Great North Advantage <ArrowRight size={15} className="ml-1.5" /></Link>
          </Button>
          <Button asChild variant="ghost" className="btn-pill text-brand-700 hover:bg-brand-50 hover:text-brand-800">
            <Link to="/who-we-are/leadership">Meet the leadership</Link>
          </Button>
        </div>
      </Reveal>
      <Reveal delay={120} className="lg:col-span-6">
        <div className="relative">
          <div className="overflow-hidden rounded-3xl">
            <img src={IMAGES.team} alt="Team collaborating" className="aspect-[5/4] w-full object-cover" />
          </div>
          <div className="absolute -bottom-6 -left-4 max-w-[260px] rounded-2xl border border-brand-100 bg-white p-5 shadow-xl shadow-brand-900/10 sm:-left-8">
            <p className="font-serif text-3xl text-brand-700">36 years</p>
            <p className="mt-1 text-sm text-ink-600">A flawless record safeguarding client funds since 1990.</p>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

const Services = () => (
  <section className="section bg-white" data-testid="services-section">
    <div className="container-x">
      <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
        <SectionHeading eyebrow="What we do" title="Shared services built for nonprofits." intro="At Great North we pride ourselves on being able to provide the following for our nonprofit clients." />
        <Link to="/what-we-do" className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-700 transition-colors hover:text-brand-900">
          All services <ArrowRight size={15} />
        </Link>
      </div>
      <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
        {SERVICES.map((s, i) => (
          <NumberedCard key={s.slug} index={i + 1} title={s.title} text={s.short} to={`/what-we-do/${s.slug}`} icon={s.icon} delay={i * 60} />
        ))}
        <Reveal delay={320}>
          <div className="flex h-full flex-col justify-between rounded-3xl bg-brand-700 p-8 text-white">
            <div>
              <span className="rounded-full border border-white/20 px-2.5 py-1 font-mono text-[11px] tracking-widest text-sand-100">06</span>
              <h3 className="mt-6 font-serif text-2xl">Special Project Management</h3>
              <p className="mt-3 text-[15px] leading-relaxed text-sand-200">From a new roof on a community theatre to a playground for an early learning program, Great North understands what it takes to manage challenging nonprofit projects.</p>
            </div>
            <Link to="/contact-us" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-gold-200 transition-colors hover:text-white">
              Discuss a project <ArrowRight size={15} />
            </Link>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const Audiences = () => (
  <section className="section" data-testid="audiences-section">
    <div className="container-x">
      <SectionHeading eyebrow="Who we serve" title="A one-stop portal for your nonprofit's administrative and management needs." />
      <div className="mt-12 grid gap-10 md:grid-cols-2">
        {AUDIENCES.map((a, i) => (
          <ImageCard key={a.slug} index={i + 1} title={a.title} text={a.short} image={a.image} to={`/who-we-serve/${a.slug}`} delay={i * 100} />
        ))}
      </div>
    </div>
  </section>
);

const WhyUs = () => (
  <section className="section bg-ink-900 text-white" data-testid="why-section">
    <div className="container-x grid gap-12 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <SectionHeading light eyebrow="Why we stand out" title="A formula that works for nonprofit organizations." intro="Clients benefit from remarkable service, extensive knowledge of the marketplace and a team of tirelessly dedicated administration and management professionals." />
        <Button asChild className="btn-pill mt-8 bg-gold-400 text-ink-900 hover:bg-gold-300">
          <Link to="/why-we-stand-out">Why Great North <ArrowRight size={15} className="ml-1.5" /></Link>
        </Button>
      </div>
      <div className="grid gap-px overflow-hidden rounded-3xl bg-white/10 sm:grid-cols-2 lg:col-span-7">
        {DIFFERENTIATORS.map((d, i) => (
          <Reveal key={d.title} delay={i * 80} className="bg-ink-900 p-7 transition-colors duration-300 hover:bg-ink-800">
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-brand-800 text-gold-200">
              <Icon name={d.icon} size={20} />
            </span>
            <h3 className="mt-5 font-serif text-xl">{d.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-sand-300">{d.text}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const FeaturedClients = () => {
  const [idx, setIdx] = useState(0);
  const count = FEATURED_CLIENTS.length;
  const next = useCallback(() => setIdx((i) => (i + 1) % count), [count]);
  const prev = () => setIdx((i) => (i - 1 + count) % count);
  useEffect(() => {
    const t = setInterval(next, 8000);
    return () => clearInterval(t);
  }, [next]);
  const c = FEATURED_CLIENTS[idx];
  return (
    <section className="section" data-testid="clients-section">
      <div className="container-x">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <SectionHeading eyebrow="Featured clients" title="Hear it from the people we serve." />
          <div className="flex items-center gap-2">
            <button onClick={prev} className="flex h-11 w-11 items-center justify-center rounded-full border border-brand-200 text-brand-700 transition-colors hover:bg-brand-600 hover:text-white hover:border-brand-600" aria-label="Previous client" data-testid="client-prev">
              <ChevronLeft size={18} />
            </button>
            <button onClick={next} className="flex h-11 w-11 items-center justify-center rounded-full border border-brand-200 text-brand-700 transition-colors hover:bg-brand-600 hover:text-white hover:border-brand-600" aria-label="Next client" data-testid="client-next">
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
        <div key={idx} className="mt-12 grid gap-8 rounded-3xl border border-brand-100 bg-white p-6 fade-in lg:grid-cols-12 lg:p-10" data-testid="client-slide">
          <div className="lg:col-span-4">
            <div className="flex aspect-[4/3] items-center justify-center overflow-hidden rounded-2xl bg-brand-50 p-6">
              <img src={c.logo} alt={c.name} className="max-h-full max-w-full object-contain" />
            </div>
            <h3 className="mt-5 font-serif text-2xl text-ink-900" data-testid="client-name">{c.name}</h3>
            <p className="mt-3 text-sm leading-relaxed text-ink-600">{c.description}</p>
          </div>
          <div className="flex flex-col justify-between lg:col-span-8 lg:pl-8 lg:border-l lg:border-brand-100">
            <div>
              <Quote size={36} className="text-brand-200" />
              <blockquote className="mt-4 font-serif text-2xl leading-snug text-ink-900 lg:text-3xl">“{c.quote}”</blockquote>
            </div>
            <div className="mt-8 flex items-center justify-between">
              <div>
                <p className="font-semibold text-ink-900">{c.author}</p>
                <p className="text-sm text-ink-600">{c.role}, {c.name}</p>
              </div>
              <div className="flex gap-1.5">
                {FEATURED_CLIENTS.map((_, i) => (
                  <button key={i} onClick={() => setIdx(i)} className={`h-2 rounded-full transition-all duration-300 ${i === idx ? "w-8 bg-brand-600" : "w-2 bg-brand-200 hover:bg-brand-400"}`} aria-label={`Go to client ${i + 1}`} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Home = () => (
  <>
    <Hero />
    <ClientLogos />
    <Stats />
    <Intro />
    <Services />
    <Audiences />
    <WhyUs />
    <FeaturedClients />
    <CTA />
  </>
);

export default Home;
